import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
// DTC-K: route the connect-inbox lifecycle write timestamp through the
// canonical clock so the persisted state-change is deterministic under
// `clock.setFixed(...)`.
import { clock } from '@/utils/clock';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Inbox, CheckCircle2, XCircle, Clock, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useMyConnectRequests, useRespondConnectRequest, useMyThreads, fetchCounterpartyShopNames, type ConnectRequest, type ConnectThread } from '@/hooks/useConnectApi';
import { ThreadView } from './ThreadView';
import { useAuth } from '@/contexts/AuthContext';

/** Resolve shop names for a list of shop IDs */
function useShopNames(shopIds: string[]) {
  const [names, setNames] = useState<Record<string, string>>({});
  useEffect(() => {
    if (shopIds.length === 0) return;
    const unique = [...new Set(shopIds)];
    // Core-100 direction 15: names only for shops this person is talking to.
    void fetchCounterpartyShopNames(unique).then(setNames);
  }, [shopIds.join(',')]);
  return names;
}

/** Resolve user display names for a list of user IDs */
function useUserNames(userIds: string[]) {
  const [names, setNames] = useState<Record<string, string>>({});
  useEffect(() => {
    if (userIds.length === 0) return;
    const unique = [...new Set(userIds)];
    supabase
      .from('profiles')
      .select('id, display_name, email')
      .in('id', unique)
      .then(({ data }) => {
        if (data) {
          const map: Record<string, string> = {};
          data.forEach((p: { id: string; display_name: string | null; email: string | null }) => {
            map[p.id] = p.display_name || p.email || 'User';
          });
          setNames(map);
        }
      });
  }, [userIds.join(',')]);
  return names;
}

/** Get unread message counts per thread.
 *
 * INA-A2: batched fetch + in-memory grouping. Pre-INA-A2 this hook fired
 * one COUNT round trip per thread via Promise.all(threads.map(...)) — a
 * classic per-thread N+1 read-side fanout that re-fired on every
 * threads-list reference change (e.g., realtime delivering a connect
 * message). Now: ONE batched connect_messages.select('thread_id,
 * sender_type, created_at').in('thread_id', threadIds) call, then
 * in-memory group preserving the EXACT per-thread lastRead + opposite
 * sender_type unread rule. For 15 threads: 15 RT → 1 RT.
 *
 * Mirrors the canonical anti-N+1 pattern at useTicketThread.ts:110-117
 * (Set + IN-clause batched fetch + in-memory decoration), already
 * established cross-workstream (OPS-A2 batched bay fallback, OPS-A3
 * batched Pensieve).
 *
 * Behavior preserved end-to-end: zero-count default for threads with no
 * qualifying messages (caller relies on `?? 0` default for the badge);
 * sender filter (passport viewer counts only `sender_type === 'shop'`,
 * shop viewer counts only `sender_type === 'user'`); timestamp filter
 * (skipped when lastRead is null/undefined — matches pre-INA-A2 `if
 * (lastRead) query = query.gt(...)`); empty-state and loading behavior
 * unchanged.
 */
function useUnreadCounts(threads: ConnectThread[] | undefined, userId: string | undefined) {
  const [counts, setCounts] = useState<Record<string, number>>({});
  useEffect(() => {
    if (!threads?.length || !userId) { setCounts({}); return; }
    const threadIds = threads.map((t) => t.id);
    // Build a per-thread context map so the in-memory grouping pass can
    // resolve the same `isPassportUser` + `lastRead` + opposite-sender
    // rule that the pre-INA-A2 per-thread query encoded.
    const threadById: Record<string, ConnectThread> = {};
    threads.forEach((t) => { threadById[t.id] = t; });
    // Initialize counts with zero defaults for every active thread id so
    // threads with no qualifying messages still appear in the map (UI
    // reads `unreadCounts[t.id] ?? 0` so a missing key would also show 0,
    // but seeding with zero keeps the map shape parity with pre-INA-A2).
    const initial: Record<string, number> = Object.fromEntries(
      threadIds.map((id) => [id, 0]),
    );
    supabase
      .from('connect_messages')
      .select('thread_id, sender_type, created_at')
      .in('thread_id', threadIds)
      .then(({ data }) => {
        if (!data) { setCounts(initial); return; }
        const grouped: Record<string, number> = { ...initial };
        for (const m of data as Array<{ thread_id: string; sender_type: string; created_at: string }>) {
          const t = threadById[m.thread_id];
          if (!t) continue; // defensive — .in() scopes to threadIds, so this should never fire
          const isPassportUser = t.user_id === userId;
          const expectedSender = isPassportUser ? 'shop' : 'user';
          if (m.sender_type !== expectedSender) continue;
          const lastRead = isPassportUser ? t.user_last_read_at : t.shop_last_read_at;
          // ISO 8601 timestamps with consistent format → lexicographic
          // comparison is equivalent to chronological comparison; matches
          // the pre-INA-A2 server-side `.gt('created_at', lastRead)`.
          if (lastRead && !(m.created_at > lastRead)) continue;
          grouped[m.thread_id] = (grouped[m.thread_id] ?? 0) + 1;
        }
        setCounts(grouped);
      });
  }, [threads, userId]);
  return counts;
}

export function ConnectionInbox() {
  const { user } = useAuth();
  const { data: requests, isLoading: reqLoading } = useMyConnectRequests();
  const { data: threads, isLoading: threadLoading } = useMyThreads();
  const respond = useRespondConnectRequest();
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const unreadCounts = useUnreadCounts(threads, user?.id);
  const totalUnread = Object.values(unreadCounts).reduce((s, c) => s + c, 0);

  // Collect all shop IDs from requests AND threads to resolve names
  const requestShopIds = (requests ?? [])
    .map(r => r.from_shop_id || r.to_shop_id)
    .filter(Boolean) as string[];
  const threadShopIds = (threads ?? []).map(t => t.shop_id).filter(Boolean);
  const shopNames = useShopNames([...requestShopIds, ...threadShopIds]);

  // Collect user IDs from threads to resolve names
  const threadUserIds = (threads ?? []).map(t => t.user_id).filter(Boolean);
  // Also collect user IDs from outgoing requests (to_user_id)
  const outgoingUserIds = (requests ?? []).map(r => r.to_user_id).filter(Boolean) as string[];
  const userNames = useUserNames([...threadUserIds, ...outgoingUserIds]);

  const incoming = requests?.filter(r => r.to_user_id === user?.id && r.status === 'pending') || [];
  const outgoing = requests?.filter(r => r.from_user_id === user?.id) || [];

  const activeThread = threads?.find(t => t.id === activeThreadId);

  // Mark thread as read when opening it
  const openThread = useCallback(async (threadId: string) => {
    setActiveThreadId(threadId);
    const thread = threads?.find(t => t.id === threadId);
    if (!thread || !user?.id) return;
    const isPassportUser = thread.user_id === user.id;
    const col = isPassportUser ? 'user_last_read_at' : 'shop_last_read_at';
    await supabase
      .from('connect_threads')
      .update({ [col]: clock.now().toISOString() } as any)
      .eq('id', threadId);
  }, [threads, user?.id]);

  if (activeThreadId) {
    return (
      <Card>
        <CardHeader className="flex-row items-center gap-2 pb-2">
          <Button variant="ghost" size="sm" onClick={() => setActiveThreadId(null)}>← Back</Button>
          <CardTitle className="text-lg">Conversation</CardTitle>
        </CardHeader>
        <CardContent>
          <ThreadView threadId={activeThreadId} senderType={activeThread?.user_id === user?.id ? 'user' : 'shop'} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Inbox className="h-5 w-5 text-primary" />
          <CardTitle className="text-lg">Connection Inbox</CardTitle>
        </div>
        <CardDescription>Manage your connection requests and conversations.</CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="incoming">
          <TabsList className="w-full">
            <TabsTrigger value="incoming" className="flex-1">
              Incoming {incoming.length > 0 && <Badge variant="secondary" className="ml-1.5">{incoming.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="outgoing" className="flex-1">Outgoing</TabsTrigger>
            <TabsTrigger value="threads" className="flex-1">
              Messages
              {totalUnread > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold min-w-[18px] h-[18px] px-1">
                  {totalUnread}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="incoming" className="mt-4 space-y-3">
            {reqLoading ? (
              <div className="flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
            ) : incoming.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No pending requests.</p>
            ) : (
              incoming.map((r) => (
                <RequestRow key={r.id} request={r} shopNames={shopNames} onRespond={(action) => respond.mutate({ request_id: r.id, action })} isPending={respond.isPending} />
              ))
            )}
          </TabsContent>

          <TabsContent value="outgoing" className="mt-4 space-y-3">
            {outgoing.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No outgoing requests.</p>
            ) : (
              outgoing.map((r) => (
                <div key={r.id} className="flex items-center justify-between rounded-lg border p-3">
                  <div>
                    <p className="text-sm font-medium">To: {r.to_shop_id ? (shopNames[r.to_shop_id] || 'Shop') : (r.to_user_id ? (userNames[r.to_user_id] || 'User') : 'User')}</p>
                    {r.message_intro && <p className="text-xs text-muted-foreground line-clamp-1">{r.message_intro}</p>}
                  </div>
                  <StatusBadge status={r.status} />
                </div>
              ))
            )}
          </TabsContent>

          <TabsContent value="threads" className="mt-4 space-y-3">
            {threadLoading ? (
              <div className="flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
            ) : !threads?.length ? (
              <p className="text-sm text-muted-foreground text-center py-4">No conversations yet.</p>
            ) : (
              threads.map((t) => (
                <button
                  key={t.id}
                  className="w-full flex items-center justify-between rounded-lg border p-3 hover:bg-accent/50 transition-colors text-left"
                  onClick={() => openThread(t.id)}
                >
                  <div>
                    <p className="text-sm font-medium">Thread: {t.user_id === user?.id ? (shopNames[t.shop_id] || 'Shop') : (userNames[t.user_id] || 'User')}</p>
                    <p className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {(unreadCounts[t.id] ?? 0) > 0 && (
                      <span className="inline-flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold min-w-[18px] h-[18px] px-1">
                        {unreadCounts[t.id]}
                      </span>
                    )}
                    <StatusBadge status={t.status} />
                  </div>
                </button>
              ))
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

function RequestRow({ request, shopNames, onRespond, isPending }: { request: ConnectRequest; shopNames: Record<string, string>; onRespond: (a: 'accept' | 'decline') => void; isPending: boolean }) {
  const fromLabel = request.from_shop_id
    ? (shopNames[request.from_shop_id] || 'Shop')
    : 'User';
  return (
    <div className="flex items-center justify-between rounded-lg border p-3">
      <div>
        <p className="text-sm font-medium">From: {fromLabel}</p>
        {request.message_intro && <p className="text-xs text-muted-foreground line-clamp-1">{request.message_intro}</p>}
        <p className="text-xs text-muted-foreground">{new Date(request.created_at).toLocaleDateString()}</p>
      </div>
      <div className="flex gap-1.5">
        <Button size="sm" variant="default" disabled={isPending} onClick={() => onRespond('accept')}>
          <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Accept
        </Button>
        <Button size="sm" variant="outline" disabled={isPending} onClick={() => onRespond('decline')}>
          <XCircle className="h-3.5 w-3.5 mr-1" /> Decline
        </Button>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
    pending: 'secondary',
    accepted: 'default',
    active: 'default',
    declined: 'destructive',
    blocked: 'destructive',
    canceled: 'outline',
    closed: 'outline',
  };
  return (
    <Badge variant={variants[status] || 'outline'} className="text-xs capitalize">
      {status === 'pending' && <Clock className="h-3 w-3 mr-1" />}
      {status}
    </Badge>
  );
}
